// Small primitives shared by the dashboard tabs: card surface, skeleton,
// change pill, inline error and empty note. The pill itself is the shared
// components/ui/Pill, in its compact size.
import { useLanguage } from "../../../contexts/LanguageContext";
import { Pill } from "../../../components/ui/Pill";

export function Panel({ label, title, subtitle, actions, className = "", children }) {
  return (
    <section
      aria-label={label || (typeof title === "string" ? title : undefined)}
      className={`flex min-w-0 flex-col gap-4 rounded-[14px] border border-gray-200 bg-white p-5 sm:px-6 ${className}`}
    >
      {(title || actions) && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            {title && (
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            )}
            {subtitle && <p className="text-[13px] leading-snug text-gray-500">{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Skeleton({ className = "" }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded-md bg-gray-100 ${className}`}
    />
  );
}

// Badge for a percentChange() result. `higherIsBetter` picks the tone.
export function ChangePill({ change, higherIsBetter = true }) {
  const { t } = useLanguage();
  if (!change || change.type === "none") {
    return <Pill size="sm" tone="muted">–</Pill>;
  }
  if (change.type === "new") {
    return <Pill size="sm" tone="muted">{t("dashboard.delta.new")}</Pill>;
  }
  const { value } = change;
  if (value === 0) {
    return <Pill size="sm" tone="muted">0%</Pill>;
  }
  const up = value > 0;
  const tone = up === higherIsBetter ? "good" : "bad";
  return (
    <Pill
      size="sm"
      tone={tone}
      srText={up ? t("dashboard.delta.increased") : t("dashboard.delta.decreased")}
    >
      <span aria-hidden="true">{up ? "↑" : "↓"}&nbsp;</span>
      {Math.abs(value)}%
    </Pill>
  );
}

// Badge for an absolute difference with a pre-formatted magnitude.
export function DiffPill({ diff, text, higherIsBetter = true }) {
  const { t } = useLanguage();
  if (diff === null || diff === undefined || !Number.isFinite(diff)) return null;
  if (diff === 0) return <Pill size="sm" tone="muted">{text}</Pill>;
  const up = diff > 0;
  return (
    <Pill
      size="sm"
      tone={up === higherIsBetter ? "good" : "bad"}
      srText={up ? t("dashboard.delta.increased") : t("dashboard.delta.decreased")}
    >
      <span aria-hidden="true">{up ? "↑" : "↓"}&nbsp;</span>
      {text}
    </Pill>
  );
}

export function SectionError({ onRetry }) {
  const { t } = useLanguage();
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-[14px] border border-gray-200 bg-white p-8 text-center"
    >
      <h2 className="text-base font-semibold text-gray-900">
        {t("dashboard.error.title")}
      </h2>
      <p className="text-sm text-gray-600">{t("dashboard.error.body")}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-11 rounded-[10px] bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          {t("dashboard.error.retry")}
        </button>
      )}
    </div>
  );
}

// react-error-boundary fallback: same copy as SectionError, translated.
export function DashboardErrorFallback({ resetErrorBoundary }) {
  return <SectionError onRetry={resetErrorBoundary} />;
}

export function EmptyNote({ children }) {
  const { t } = useLanguage();
  return (
    <p className="rounded-[10px] bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
      {children || t("dashboard.empty")}
    </p>
  );
}

// A row of toggle buttons (range picker, chart metric, filters).
export function SegmentedControl({ label, options, value, onChange, dark = false }) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`inline-flex max-w-full flex-wrap gap-0.5 rounded-[11px] p-[3px] ${
        dark ? "border border-gray-200 bg-white" : "bg-gray-100"
      }`}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={`min-h-9 rounded-lg px-3.5 text-[13px] font-medium whitespace-nowrap transition-colors ${
              selected
                ? dark
                  ? "bg-gray-900 text-white"
                  : "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
